// Typed mock data for EPIC-008/009 (Import Work Order CSV / Apply Work Order
// Import). Draft only — parsing and validation happen entirely client-side;
// no row is ever sent anywhere. Shape mirrors `import_row`
// (src/db/schema/handover/import-row.ts) exactly: item, quantity, location,
// start_date, end_date.

import { findItem, findLocation } from "@/mocks/mass-update-reference";

export interface ImportRow {
  id: string;
  rowNumber: number;
  item: string;
  quantity: string;
  location: string;
  startDate: string;
  endDate: string;
  isValid: boolean;
  errors: string[];
}

const EXPECTED_HEADER = ["item", "quantity", "location", "start_date", "end_date"];

export const IMPORT_CSV_TEMPLATE = EXPECTED_HEADER.join(",");

export const SAMPLE_IMPORT_CSV = [
  IMPORT_CSV_TEMPLATE,
  "JPC-2.0-M,25,MAIN-WH,2026-09-22,2026-10-06",
  "AVS-BASE,10,PROD-FL1,2026-09-24,2026-10-08",
  "ATP-9000,15,MAIN-WH,2026-09-25,2026-10-09",
  "G4-PANT-32,-5,PROD-FL2,2026-09-26,2026-10-10",
  "COMBAT-SHIRT-L,20,ORBIT-STATION,2026-09-27,2026-10-11",
].join("\n");

function validateRow(item: string, quantity: string, location: string): string[] {
  const errors: string[] = [];
  if (!item || !findItem(item)) errors.push(`Unknown item "${item || "(blank)"}"`);
  if (!location || !findLocation(location)) {
    errors.push(`Unknown location "${location || "(blank)"}"`);
  }
  const quantityNumber = Number(quantity);
  if (!quantity || Number.isNaN(quantityNumber) || quantityNumber <= 0) {
    errors.push(`Invalid quantity "${quantity || "(blank)"}"`);
  }
  return errors;
}

// Minimal hand-rolled CSV parser — the import rows have no embedded commas
// or quoting to worry about, so a straight split is enough here.
export function parseImportCsv(text: string): ImportRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (lines.length === 0) return [];

  const header = (lines[0] ?? "").split(",").map((cell) => cell.trim().toLowerCase());
  const dataLines = header.join(",") === EXPECTED_HEADER.join(",") ? lines.slice(1) : lines;

  return dataLines.map((line, index) => {
    const [item = "", quantity = "", location = "", startDate = "", endDate = ""] = line
      .split(",")
      .map((cell) => cell.trim());
    const errors = validateRow(item, quantity, location);
    return {
      id: `import-row-${index + 1}`,
      rowNumber: index + 1,
      item,
      quantity,
      location,
      startDate,
      endDate,
      isValid: errors.length === 0,
      errors,
    };
  });
}
